import React, { useEffect, useState, useLayoutEffect } from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { PatternlibSpinner } from "@liebherr/patternlib-react";
import styles from "./App.module.css";
import Start from "./pages/start/Start";
import Configurator from "./pages/configurator/Configurator";
import Summary from "./pages/summary/Summary";
import Compare from "./pages/compare/Compare";
import MyConfigs from "./pages/myconfigs/MyConfigs";
import Downloads from "./pages/downloads/Downloads";
import Finder from "./pages/finder/Finder";
// import Profile from "./pages/profile/Profile";
import Sticky from "./components/sticky/Sticky";
import Confirm from "./pages/confirm/Confirm";
import Footer from "./components/footer/Footer";
import Contact from "./pages/contact/Contact";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { useLocalStorage } from "./helpers/helpers";
import RequireAuth from "./components/require-auth/RequireAuth";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import Imprint from "./pages/imprint/Imprint";
import useMutationObserver from "./hooks/useMutationObserver";

const Wrapper = ({ children }) => {
  const location = useLocation();
  useLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);
  return children;
};

export default function App() {
  const auth = useAuth();
  const [profile, setProfile] = useState(false);
  const [isDemoUser, setIsDemoUser] = useState(false);
  const navigate = useNavigate();
  const [latestConfig, setLatestConfig] = useLocalStorage("key", null);

  const [userData, setUserData] = useState(); // store the user data

  const location = useLocation();
  const { t, i18n } = useTranslation();

  // use mutation observer to get current screen size
  const options = {
    config: { attributes: true, childList: true, subtree: true },
  };
  const target = document.documentElement;
  const attributeName = "breakpoint";
  const screen = useMutationObserver(options, target, attributeName);

  // if pathname is /demo set state
  useEffect(() => {
    if (window.location.pathname === "/demo") {
      setIsDemoUser(true);
    }
  }, []);

  // automatically sign-in if the pathname is /login
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading
    ) {
      if (window.location.pathname === "/login") {
        auth.signinRedirect();
      }
    }
  }, [
    auth.isAuthenticated,
    auth.activeNavigator,
    auth.isLoading,
    auth.signinRedirect,
  ]);

  // Get the userData
  const getUserData = async (token) => {
    try {
      // Rquest to get more user data (via graphql) after successfull login
      const query = JSON.stringify({
        query: `{
          me {
            mail
            firstName
            lastName
            company {
              street
              postalCode
              locality
              country
              telephoneNumber
              companyName
            }
          }
        }`,
      });

      const options = {
        method: "POST",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: query,
      };

      const response = await fetch(window.env.REACT_APP_GRAPHQL_URL, options);

      if (response.ok) {
        const res = await response.json();

        // set the user data
        setUserData(res.data);
      }
    } catch (err) {
      console.log(err);
      setUserData(null);
    }
  };

  useEffect(() => {
    if (auth.isAuthenticated) {
      getUserData(auth.user.access_token);
      setProfile({ ...auth.user.profile });
      setIsDemoUser(auth.user.profile["role"] === "DemoUser"); // check if the user is a dem user
    }

    return () => {
      setUserData(null);
      setProfile(null);
      setIsDemoUser(false);
    };
  }, [auth]);

  useEffect(() => {
    // the `return` is important - addAccessTokenExpiring() returns a cleanup function
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.signinSilent]);

  useEffect(() => {
    // if user get redirected after login direct user back to previous location
    return auth.events.addUserLoaded((auth) => {
      if (latestConfig[1] === "/downloads") {
        // read key from local storage
        const part = latestConfig[0].part;
        const partkey = latestConfig[0].partkey;
        const projectPath = latestConfig[0].projectPath;

        navigate("/downloads", {
          state: { key: { part, partkey, projectPath } },
        });
      } else if (latestConfig[1] === "/summary") {
        // read key from local storage
        const partkey = latestConfig[0].partkey;
        const projectPath = latestConfig[0].projectPath;

        // navigate to summary page
        navigate(`/summary/${projectPath}`, {
          state: { key: partkey, isOpen: true },
        });
      }
      setLatestConfig([null, null]);
    });
  }, [auth.signinRedirect]);

  if (auth.isLoading) {
    return (
      <div className={styles.login}>
        <div className={styles.gridContainer}>
          <div className={styles.item1 && styles.gridItem}>
            <PatternlibSpinner spin></PatternlibSpinner>
          </div>
          <div className={styles.item2 && styles.gridItem}>
            {t("Spinner.loadProduct")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* prevent cmp on imprint page */}
      {location.pathname === "/imprint" ? (
        <Helmet>
          <script type="text/javascript">
            {`
            var UC_UI_SUPPRESS_CMP_DISPLAY=true;
          `}
          </script>
        </Helmet>
      ) : (
        ""
      )}

      {/* Initialize cmp script */}
      <Helmet>
        <script
          type="text/javascript"
          id="cookieboxUC"
          language={
            i18n.language === "de-DE" || i18n.language === "de" ? "de" : "en"
          }
          src="https://static.liebherr.com/cmp/master/cmp.js"
          config="https://static.liebherr.com/cmp/master/configs/cok-konfigurator.json"
        ></script>
      </Helmet>
      {screen !== "isMobile" && !isDemoUser && <Sticky></Sticky>}
      <div
        className={styles.container}
        style={
          location.pathname === "/" ||
          location.pathname === "/myconfigs" ||
          // location.pathname === "/profile" ||
          location.pathname === "/contact" ||
          location.pathname === "/imprint"
            ? { backgroundColor: "var(--color-steel-50)" }
            : { backgroundColor: "white" }
        }
      >
        <Wrapper>
          <Routes>
            <Route
              path="/"
              element={<Start profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/login"
              element={<Start profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/demo"
              element={<Start profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/:projectPath"
              element={<Configurator isDemoUser={isDemoUser} />}
            />
            <Route
              path="/summary/:projectPath"
              element={<Summary isDemoUser={isDemoUser} userData={userData} />}
            />
            <Route
              path="/myconfigs"
              element={<MyConfigs profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/compare"
              element={<Compare isDemoUser={isDemoUser} />}
            />
            <Route
              path="/finder"
              element={<Finder isDemoUser={isDemoUser} />}
            />
            {/* <Route path="/profile" element={<Profile />} /> */}
            <Route path="/confirm" element={<Confirm />} />
            <Route
              path="/contact"
              element={<Contact profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/imprint"
              element={<Imprint profile={profile} isDemoUser={isDemoUser} />}
            />
            <Route
              path="/downloads"
              element={
                <RequireAuth>
                  <Downloads />
                </RequireAuth>
              }
            />
          </Routes>
        </Wrapper>
        <Footer
          margin={
            location.pathname === "/myconfigs" ||
            location.pathname === "/compare" ||
            location.pathname === "/contact"
              ? screen === "isMobile"
                ? "0"
                : "0 0 0 80px"
              : "0"
          }
        ></Footer>
      </div>
    </>
  );
}
